// LICENSE

// (The MIT License)

// Copyright (c) 2021 Matias Meno <m@tias.me>
// Logo (c) 2015 "1910" www.weare1910.com

// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import LoadingOverlay from "react-loading-overlay";
import { getDataTransferItems as defaultGetDataTransferItem } from "./utils";
import { useDropzone } from "./hooks/useDropzone";
import { getOverlayStyles, inputStyles } from "./styles";
import { useDropzoneStyles } from "./hooks/useDropzoneStyles";

const Dropzone = props => {
  const { accept, children, disabled, inputProps, name } = props;

  const {
    node,
    fileInputEl,
    acceptedFiles,
    rejectedFiles,
    isDragActive,
    isProcessing,
    error,
    onDragStart,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDropHandler,
    onClickHandler,
  } = useDropzone(props);

  const { className, appliedStyle, divProps, isDragAccept, isDragReject } = useDropzoneStyles(
    props
  );

  const isNewUI = useSelector(state => state.common.clientSettings.newUIFlag);

  const onKeyDown = useCallback(
    event => {
      if (disabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClickHandler(event);
      }
    },
    [disabled, onClickHandler]
  );

  const composeHandler = useCallback(
    handler => {
      if (disabled) {
        return null;
      }
      return handler;
    },
    [disabled]
  );

  const renderChildren = useCallback(
    (isDragActive, isDragAccept, isDragReject) => {
      if (typeof children === "function") {
        return children({
          isDragActive,
          isDragAccept,
          isDragReject,
          acceptedFiles,
          rejectedFiles,
        });
      }
      return children;
    },
    [children, acceptedFiles, rejectedFiles]
  );

  const inputAttributes = {
    disabled,
    accept,
    type: "file",
    style: {
      ...inputStyles,
      ...inputProps.style,
    },
    multiple: false,
    ref: fileInputEl,
    onChange: onDropHandler,
    autoComplete: "off",
  };

  if (name && name.length) {
    inputAttributes.name = name;
  }

  return (
    <LoadingOverlay
      active={isProcessing && isNewUI}
      spinner
      text="Processing your file..."
      styles={{
        overlay: base => ({
          ...base,
          ...getOverlayStyles,
        }),
      }}
    >
      <div
        className={className}
        style={appliedStyle}
        {...divProps}
        onClick={composeHandler(onClickHandler)}
        onDragStart={composeHandler(onDragStart)}
        onDragEnter={composeHandler(onDragEnter)}
        onDragOver={composeHandler(onDragOver)}
        onDragLeave={composeHandler(onDragLeave)}
        onDrop={composeHandler(onDropHandler)}
        onKeyDown={composeHandler(onKeyDown)}
        ref={node}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
      >
        {renderChildren(isDragActive, isDragAccept, isDragReject)}

        <input {...inputProps} {...inputAttributes} />

        {error && (
          <div className="field__error">
            <p>
              <strong>{error}</strong>
            </p>
          </div>
        )}
      </div>
    </LoadingOverlay>
  );
};

Dropzone.propTypes = {
  /**
   * Allow specific types of files. See https://github.com/okonet/attr-accept for more information.
   * Keep in mind that mime type determination is not reliable across platforms. CSV files,
   * for example, are reported as text/plain under macOS but as application/vnd.ms-excel under
   * Windows. In some cases there might not be a mime type set at all.
   * See: https://github.com/react-dropzone/react-dropzone/issues/276
   */
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),

  /**
   * Contents of the dropzone
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * Disallow clicking on the dropzone container to open file dialog
   */
  disableClick: PropTypes.bool,

  /**
   * Enable/disable the dropzone entirely
   */
  disabled: PropTypes.bool,

  /**
   * Enable/disable preview generation
   */
  disablePreview: PropTypes.bool,

  /**
   * If false, allow dropped items to take over the current browser window
   */
  preventDropOnDocument: PropTypes.bool,

  /**
   * Pass additional attributes to the `<input type="file"/>` tag
   */
  inputProps: PropTypes.object,

  /**
   * Allow dropping multiple files
   */
  multiple: PropTypes.bool,

  /**
   * `name` attribute for the input tag
   */
  name: PropTypes.string,

  /**
   * Maximum file size (in bytes)
   */
  maxSize: PropTypes.number,

  /**
   * Minimum file size (in bytes)
   */
  minSize: PropTypes.number,

  /**
   * className
   */
  className: PropTypes.string,

  /**
   * className to apply when drag is active
   */
  activeClassName: PropTypes.string,

  /**
   * className to apply when drop will be accepted
   */
  acceptClassName: PropTypes.string,

  /**
   * className to apply when drop will be rejected
   */
  rejectClassName: PropTypes.string,

  /**
   * className to apply when dropzone is disabled
   */
  disabledClassName: PropTypes.string,

  /**
   * CSS styles to apply
   */
  style: PropTypes.object,

  /**
   * CSS styles to apply when drag is active
   */
  activeStyle: PropTypes.object,

  /**
   * CSS styles to apply when drop will be accepted
   */
  acceptStyle: PropTypes.object,

  /**
   * CSS styles to apply when drop will be rejected
   */
  rejectStyle: PropTypes.object,

  /**
   * CSS styles to apply when dropzone is disabled
   */
  disabledStyle: PropTypes.object,

  /**
   * getDataTransferItems handler
   * @param {Event} event
   * @returns {Array} array of File objects
   */
  getDataTransferItems: PropTypes.func,

  /**
   * onClick callback
   * @param {Event} event
   */
  onClick: PropTypes.func,

  /**
   * onDrop callback
   */
  onDrop: PropTypes.func,

  /**
   * onDropAccepted callback
   */
  onDropAccepted: PropTypes.func,

  /**
   * onDropRejected callback
   */
  onDropRejected: PropTypes.func,

  /**
   * onDragStart callback
   */
  onDragStart: PropTypes.func,

  /**
   * onDragEnter callback
   */
  onDragEnter: PropTypes.func,

  /**
   * onDragOver callback
   */
  onDragOver: PropTypes.func,

  /**
   * onDragLeave callback
   */
  onDragLeave: PropTypes.func,

  /**
   * Provide a callback on clicking the cancel button of the file dialog
   */
  onFileDialogCancel: PropTypes.func,
};

Dropzone.defaultProps = {
  preventDropOnDocument: true,
  disabled: false,
  disablePreview: false,
  disableClick: false,
  inputProps: {},
  multiple: false,
  maxSize: Infinity,
  minSize: 0,
  getDataTransferItems: defaultGetDataTransferItem,
};

export default Dropzone;



// WEBPACK FOOTER //
// ./components/common/file-uploader/drop-zone.js